const DEFAULT_REPORT_ATTR = 'mreport'; // 上报元素属性
const CLICK_EVENT_ATTR = 'event-click'; // 上报点击事件属性
const IMPRESSION_EVENT_ATTR = 'event-impression'; // 上报曝光事件属性
const DISABLED_ATTR = 'report-disabled'; // 上报禁用属性
const IMPRESSION_INTERVAL = 500; // 检查曝光间隔
const MODIFIER_PREFIX = '--'; // 修饰符前缀，比如：--once

// 元素属性前缀和 reportData 字段的对应表
/* eslint-disable @typescript-eslint/naming-convention */
const PREFIX_DATA_MAP = Object.entries({
    'user-': 'user',
    'prop-': 'properties',
    'content-': 'content'
});
/* eslint-disable */
// 默认上报属性对象
const defaultAttrsObj = {
    click: {
        on: false,
        stop: false,
        once: false,
        disabled: false
    },
    impression: {
        on: false,
        once: false,
        disabled: false
    },
    reportData: {}
};

// 在 data 中初始化上报数据结构
for (const [, mapKey] of PREFIX_DATA_MAP) {
    defaultAttrsObj.reportData[mapKey] = {};
}

class AutoReport {
    /**
     * @param reporter 上报对象
     * @param reportAttr 需要上报元素的属性值
     */
    constructor({reporter, reportAttr = DEFAULT_REPORT_ATTR}) {
        this.reporter = reporter;
        this.reportAttr = reportAttr;
        this.viewportHeight = document.documentElement.clientHeight;
        this.viewportWidth = document.documentElement.clientWidth;

        this.init();
    }

    /**
     * 初始化
     */
    init() {
        // 在 window 上捕捉点击事件对象
        window.addEventListener('click', this.clickEventListener.bind(this), true);

        // 定时检查曝光元素
        this.impressionEventListener();
    }

    /**
     * 处理点击上报
     * @param e event
     */
    clickEventListener(e) {
        this.nextTick(() => {
            let currentTarget = e.target;
            while (currentTarget) {
                const isReportEle = currentTarget.hasAttribute(this.reportAttr);

                if (isReportEle) {
                    const attributes = currentTarget.attributes;
                    const attrsObj = this.handleAttrsObj(attributes);

                    if (attrsObj.click.on) {
                        if (!attrsObj.click.disabled) {
                            if (attrsObj.click.once) {
                                currentTarget.setAttribute(`${DISABLED_ATTR}${MODIFIER_PREFIX}click`, 'true');
                            }

                            this.reporter.report('click', attrsObj.reportData);
                        }

                        // stop 说明不再往父级找，理解为阻止冒泡
                        if (attrsObj.click.stop) {
                            break;
                        }
                    }
                }

                // 一直往父级上找需要上报的元素
                currentTarget = currentTarget.parentElement;
            }
        });
    }

    /**
     * 处理曝光上报
     */
    impressionEventListener() {
        this.ob = new IntersectionObserver(
            entries => {
                entries.forEach(currentEntry => {
                    const {target: element, isIntersecting} = currentEntry;
                    if (isIntersecting) {
                        const attributes = element.attributes;
                        const attrsObj = this.handleAttrsObj(attributes);
                        const reportData = attrsObj.reportData;
                        if (this.reporter.beforeSend) {
                            this.reporter.beforeSend(reportData);
                            this.reporter.report('impression', reportData);
                        }
                        if (attrsObj.impression.once) {
                            this.ob.unobserve(element);
                        }
                    }
                });
            },
            {
                threshold: 0.5
            }
        );
        const reportAttr = this.reportAttr;
        setInterval(() => {
            const reportElements = document.querySelectorAll(`[${reportAttr}]`);
            const elementArr = Array.from(reportElements).filter(element => {
                return !element.getAttribute('is-register-observe');
            });
            elementArr.forEach(element => {
                element.setAttribute('is-register-observe', true);
                this.ob.observe(element);
            });
        }, IMPRESSION_INTERVAL);
    }

    /**
     * 处理元素的上报属性和数据
     * @param attributes Element.attributes 对象
     */
    handleAttrsObj(attributes) {
        const attrsObj = JSON.parse(JSON.stringify(defaultAttrsObj));

        for (let i = 0, l = attributes.length; i < l; i++) {
            const attr = attributes[i];
            let nodeName = attr.nodeName;
            const nodeValue = attr.nodeValue;

            const nodeNameArr = nodeName.split(MODIFIER_PREFIX); // 'event-click--stop--once' => ['event-click', 'stop', 'once']
            const modifiers = nodeNameArr.splice(1); // 描述符，['stop', 'once']
            nodeName = nodeNameArr[0]; // 'event-click'
            switch (nodeName) {
                case CLICK_EVENT_ATTR:
                    attrsObj.click.on = true;
                    modifiers.forEach(key => (attrsObj.click[key] = true));
                    break;

                case IMPRESSION_EVENT_ATTR:
                    attrsObj.impression.on = true;
                    modifiers.forEach(key => (attrsObj.impression[key] = true));
                    break;

                case DISABLED_ATTR:
                    if (modifiers.length > 0) {
                        // 比如，report-disabled--click="true"，只对指定事件类型生效
                        modifiers.forEach(type => {
                            if (attrsObj[type]) {
                                attrsObj[type].disabled = nodeValue === 'true';
                            }
                        });
                    } else {
                        // report-disabled="true"，对所有事件类型生效
                        attrsObj.click.disabled = attrsObj.impression.disabled = nodeValue === 'true';
                    }
                    break;

                default:
                    for (const [prefix, mapKey] of PREFIX_DATA_MAP) {
                        // 筛选出其他上报数据，组装 reportData
                        if (nodeName.startsWith(prefix)) {
                            const dataName = nodeName.slice(prefix.length);
                            const isParse = modifiers.includes('parse');

                            try {
                                attrsObj.reportData[mapKey][dataName] = isParse ? JSON.parse(nodeValue) : nodeValue;
                            } catch (e) {
                                attrsObj.reportData[mapKey][dataName] = nodeValue;
                                console.error(e);
                            }

                            break;
                        }
                    }
            }
        }

        return attrsObj;
    }

    /**
     * 判断元素是否在视口内
     * @param element Element 元素
     * @return {boolean}
     */
    isInViewport(element) {
        let inViewport = false;

        // 不是 Element 或者 display: none
        if (!(element instanceof Element) || this.isEleHidden(element)) {
            return inViewport;
        }

        const vh = this.viewportHeight || document.documentElement.clientHeight;
        const vw = this.viewportWidth || document.documentElement.clientWidth;
        const domRect = element.getBoundingClientRect();

        // 检查元素与视口是否有交集
        if (domRect.top < vh && domRect.bottom > 0 && domRect.left < vw && domRect.right > 0) {
            const domCenterY = domRect.top + domRect.height / 2;
            const domCenterX = domRect.left + domRect.width / 2;
            inViewport = domCenterY > 0 && domCenterY < vh && domCenterX > 0 && domCenterX < vw; // 元素中心坐标是否在视口内

            if (!inViewport) {
                const domViewHeight = (domRect.bottom > vh ? vh : domRect.bottom) - (domRect.top < 0 ? 0 : domRect.top);
                const domViewWidth = (domRect.right > vw ? vw : domRect.right) - (domRect.left < 0 ? 0 : domRect.left);
                inViewport = (domViewHeight * domViewWidth) / (vh * vw) > 0.5; // 元素是否占视口 50% 面积以上
            }
        }

        return inViewport;
    }

    /**
     * 为了不占用当前主线程执行，异步执行回调
     * @param cb
     */
    nextTick(cb) {
        if (typeof Promise === 'function') {
            Promise.resolve().then(cb);
        } else {
            setTimeout(cb, 0);
        }
    }

    /**
     * 检测元素是否隐藏 display: none （position为fixed的元素除外）
     * @param element 元素
     * @return {boolean}
     */
    isEleHidden(element) {
        const getComputedStyle = document.defaultView.getComputedStyle;
        const computedStyle = (typeof getComputedStyle === 'function' && getComputedStyle(element)) || {};
        return element.offsetParent === null && computedStyle.position !== 'fixed';
    }
}

export default AutoReport;
